'use client';

import { c as _c } from "react/compiler-runtime";
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Collapsible } from '@repo/design-system/components/ui/collapsible';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useGetFoundPinsQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { setHighlightedPinIds } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId, selectMapData, selectSelectedMarkerId } from '@repo/store/maps/slices/mapData';
import type { SelectLocationSource } from '@repo/types/analytic-events/maps-analytic-events';
import type { WebLoginPromptTrigger } from '@repo/types/analytic-events/website-analytic-events';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { getPinImageUrl } from '@repo/utility/image-urls';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMapControls } from '../game-map/hooks/useMapControls';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { MapDiscoveryProgress } from '../map-discovery-progress';
import { SidebarHeroFrame } from '../utils/sidebar-hero-frame';
import { ChecklistFeedback, type ChecklistFeedbackSource } from './checklist-feedback';
import { ChecklistItemRow } from './checklist-item-row';
import { SearchFilters } from './checklist-search-filters';
import { buildCategoryMap, buildCheckedItems, buildSubcategoryMap, type ChecklistItem, type CompletionFilter, filterChecklistItems, groupItemsByCategory } from './checklist-utils';
import { useChecklistFeedback } from './use-checklist-feedback';
import { useChecklistToggle } from './use-checklist-toggle';
export interface PinListSidebarShellProps {
  backgroundImageUrl: string;
  isMobilePane?: boolean;
  onBack: () => void;
  onSearchFocus?: () => void;
  headerAction?: React.ReactNode;

  /** Pin features to render. Caller pre-filters as needed. */
  pinFeatures: MapDataLocationFeature[];

  /** Content rendered at the top of the frame (icon/title/description/etc.). */
  hero: React.ReactNode;

  /** Extra content rendered between the search filters and the grouped item list. */
  beforeItems?: React.ReactNode;

  /** Hide the search/filter row. */
  disableSearch?: boolean;

  /** Item toggle clicks open the login modal (read-only presentation, e.g. shared list). */
  readOnly?: boolean;

  /** Swap each row's checkbox for a remove-X button. */
  editMode?: boolean;
  onRemovePin?: (id: string) => void;
  removeLabel?: string;

  /** When provided, shows the feedback widget at the bottom. */
  feedbackSource?: ChecklistFeedbackSource;
  feedbackAnchorLocationId?: string;
  feedbackSubcategoryId?: string;

  /** Analytics source forwarded to `selectMarkerInMap` on row click. */
  selectSource: SelectLocationSource;

  /**
   * Identity for bounds-fitting. When this value changes (and is truthy), the shell
   * fits the map to the current items once. Pass a stable key (e.g. checklist id) for
   * one-shot fit-on-mount, or a changing key (e.g. anchor pin id) for refit on change.
   */
  boundsFitKey?: string | null;

  /**
   * Controlled login-modal state. When provided, the caller owns the modal open flag
   * (and can trigger it from their own header-action handlers). When omitted, the
   * shell manages it internally for row-toggle and progress-click gating.
   */
  isLoginModalOpen?: boolean;
  onLoginModalOpenChange?: (open: boolean) => void;
  loginModalTrigger?: WebLoginPromptTrigger;
  onLoginModalTriggerChange?: (trigger: WebLoginPromptTrigger) => void;
}
export const PinListSidebarShell: React.FC<PinListSidebarShellProps> = t0 => {
  const $ = _c(107);
  const {
    backgroundImageUrl,
    isMobilePane: t1,
    onBack,
    onSearchFocus,
    headerAction,
    pinFeatures,
    hero,
    beforeItems,
    disableSearch: t2,
    readOnly: t3,
    editMode: t4,
    onRemovePin,
    removeLabel,
    feedbackSource,
    feedbackAnchorLocationId,
    feedbackSubcategoryId,
    selectSource,
    boundsFitKey: t5,
    isLoginModalOpen: controlledLoginModalOpen,
    onLoginModalOpenChange: controlledSetLoginModalOpen,
    loginModalTrigger: t6,
    onLoginModalTriggerChange
  } = t0;
  const isMobilePane = t1 === undefined ? false : t1;
  const disableSearch = t2 === undefined ? false : t2;
  const readOnly = t3 === undefined ? false : t3;
  const editMode = t4 === undefined ? false : t4;
  const boundsFitKey = t5 === undefined ? null : t5;
  const loginModalTrigger = t6 === undefined ? "checklist-item" : t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["game_map"];
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const {
    t
  } = useT(t7);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const isLoggedIn = useAppSelector(_temp);
  const {
    selectMarkerInMap
  } = useMapPinOperations();
  const {
    fitBounds
  } = useMapControls();
  const dispatch = useAppDispatch();
  const [uncontrolledLoginModalOpen, setUncontrolledLoginModalOpen] = useState(false);
  const isLoginModalOpen = controlledLoginModalOpen ?? uncontrolledLoginModalOpen;
  let t8;
  if ($[1] !== controlledLoginModalOpen || $[2] !== controlledSetLoginModalOpen) {
    t8 = open => {
      controlledSetLoginModalOpen?.(open);
      if (controlledLoginModalOpen === undefined) {
        setUncontrolledLoginModalOpen(open);
      }
    };
    $[1] = controlledLoginModalOpen;
    $[2] = controlledSetLoginModalOpen;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const setIsLoginModalOpen = t8;
  let t9;
  if ($[4] !== onLoginModalTriggerChange || $[5] !== setIsLoginModalOpen) {
    t9 = trigger => {
      onLoginModalTriggerChange?.(trigger);
      setIsLoginModalOpen(true);
    };
    $[4] = onLoginModalTriggerChange;
    $[5] = setIsLoginModalOpen;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const openLoginModal = t9;
  const [searchQuery, setSearchQuery] = useState("");
  const [completionFilter, setCompletionFilter] = useState("all");
  const searchInputRef = useRef(null);
  let t10;
  bb0: {
    if (!mapData) {
      t10 = null;
      break bb0;
    }
    let t11;
    if ($[7] !== mapData) {
      t11 = buildSubcategoryMap(mapData);
      $[7] = mapData;
      $[8] = t11;
    } else {
      t11 = $[8];
    }
    let t12;
    if ($[9] !== mapData) {
      t12 = buildCategoryMap(mapData);
      $[9] = mapData;
      $[10] = t12;
    } else {
      t12 = $[10];
    }
    let t13;
    if ($[11] !== t11 || $[12] !== t12) {
      t13 = {
        subcategoryMap: t11,
        categoryMap: t12
      };
      $[11] = t11;
      $[12] = t12;
      $[13] = t13;
    } else {
      t13 = $[13];
    }
    t10 = t13;
  }
  const lookups = t10;
  const t11 = !isLoggedIn || !externalMapId;
  let t12;
  if ($[14] !== t11) {
    t12 = {
      skip: t11
    };
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const {
    data: foundPinsData
  } = useGetFoundPinsQuery(externalMapId ?? "", t12);
  let t13;
  if ($[16] !== foundPinsData || $[17] !== isLoggedIn) {
    t13 = buildCheckedItems(isLoggedIn, foundPinsData);
    $[16] = foundPinsData;
    $[17] = isLoggedIn;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const checkedItems = t13;
  const handleToggleItem = useChecklistToggle(externalMapId, checkedItems);
  let t14;
  if ($[19] !== handleToggleItem || $[20] !== isLoggedIn || $[21] !== openLoginModal || $[22] !== readOnly) {
    t14 = itemId => {
      if (readOnly || !isLoggedIn) {
        openLoginModal("checklist-item");
        return;
      }
      handleToggleItem(itemId);
    };
    $[19] = handleToggleItem;
    $[20] = isLoggedIn;
    $[21] = openLoginModal;
    $[22] = readOnly;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const handleToggleWithLogin = t14;
  let t15;
  bb1: {
    if (!lookups) {
      let t16;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = [];
        $[24] = t16;
      } else {
        t16 = $[24];
      }
      t15 = t16;
      break bb1;
    }
    let items;
    if ($[25] !== lookups || $[26] !== pinFeatures) {
      items = [];
      for (const feature of pinFeatures) {
        if (feature.geometry.type !== "Point") {
          continue;
        }
        const subcategory = lookups.subcategoryMap.get(feature.properties.subcategoryExternalId);
        const category = subcategory ? lookups.categoryMap.get(subcategory.categoryExternalId) : undefined;
        if (!subcategory || !category) {
          continue;
        }
        const coords = feature.geometry.coordinates;
        items.push({
          id: feature.properties.externalId,
          title: feature.properties.title || subcategory.title,
          description: feature.properties.description || "",
          categoryTitle: category.title,
          subcategoryTitle: subcategory.title,
          subcategoryExternalId: subcategory.externalId,
          categoryColor: category.color,
          imagePath: feature.properties.imagePath ?? null,
          imageId: feature.properties.imageId ?? null,
          imageUrl: getPinImageUrl(feature.properties.imageId, feature.properties.imagePath) ?? null,
          coordinates: [coords[0] as number, coords[1] as number]
        });
      }
      $[25] = lookups;
      $[26] = pinFeatures;
      $[27] = items;
    } else {
      items = $[27];
    }
    t15 = items;
  }
  const allItems = t15;
  let t16;
  let t17;
  if ($[28] !== allItems || $[29] !== dispatch) {
    t16 = () => {
      const ids = allItems.map(_temp2);
      dispatch(setHighlightedPinIds(ids));
      return () => {
        dispatch(setHighlightedPinIds([]));
      };
    };
    t17 = [allItems, dispatch];
    $[28] = allItems;
    $[29] = dispatch;
    $[30] = t16;
    $[31] = t17;
  } else {
    t16 = $[30];
    t17 = $[31];
  }
  useEffect(t16, t17);
  const fittedForKeyRef = useRef(null);
  let t18;
  let t19;
  if ($[32] !== allItems || $[33] !== boundsFitKey || $[34] !== fitBounds) {
    t18 = () => {
      if (!boundsFitKey) {
        fittedForKeyRef.current = null;
        return;
      }
      if (fittedForKeyRef.current === boundsFitKey) {
        return;
      }
      if (allItems.length === 0) {
        return;
      }
      const lons = allItems.map(_temp3);
      const lats = allItems.map(_temp4);
      const sw = [Math.min(...lons), Math.min(...lats)];
      const ne = [Math.max(...lons), Math.max(...lats)];
      fittedForKeyRef.current = boundsFitKey;
      fitBounds([sw, ne], {
        padding: 120,
        maxZoom: 8,
        duration: 800
      });
    };
    t19 = [boundsFitKey, allItems, fitBounds];
    $[32] = allItems;
    $[33] = boundsFitKey;
    $[34] = fitBounds;
    $[35] = t18;
    $[36] = t19;
  } else {
    t18 = $[35];
    t19 = $[36];
  }
  useEffect(t18, t19);
  const totalCount = allItems.length;
  let t20;
  if ($[37] !== allItems || $[38] !== checkedItems) {
    let t21;
    if ($[40] !== checkedItems) {
      t21 = (n, item_0) => n + (checkedItems.has(item_0.id) ? 1 : 0);
      $[40] = checkedItems;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    t20 = allItems.reduce(t21, 0);
    $[37] = allItems;
    $[38] = checkedItems;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const foundCount = t20;
  let t21;
  if ($[42] !== allItems || $[43] !== checkedItems || $[44] !== completionFilter || $[45] !== searchQuery) {
    t21 = groupItemsByCategory(filterChecklistItems(allItems, searchQuery, completionFilter, checkedItems));
    $[42] = allItems;
    $[43] = checkedItems;
    $[44] = completionFilter;
    $[45] = searchQuery;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const groupedItems = t21;
  let t22;
  if ($[47] !== groupedItems) {
    t22 = Object.keys(groupedItems);
    $[47] = groupedItems;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const hasAnyItems = t22.length > 0;
  let t23;
  if ($[49] !== feedbackAnchorLocationId || $[50] !== feedbackSubcategoryId) {
    t23 = {
      anchorLocationId: feedbackAnchorLocationId,
      subcategoryId: feedbackSubcategoryId
    };
    $[49] = feedbackAnchorLocationId;
    $[50] = feedbackSubcategoryId;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  const handleFeedbackRate = useChecklistFeedback(feedbackSource ?? "explore-list", t23);
  let t24;
  if ($[52] !== t) {
    t24 = t(_temp5);
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t) {
    t25 = t(_temp6);
    $[54] = t;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== isLoggedIn || $[57] !== openLoginModal) {
    t26 = !isLoggedIn ? () => openLoginModal("checklist-item") : undefined;
    $[56] = isLoggedIn;
    $[57] = openLoginModal;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== foundCount || $[60] !== t26 || $[61] !== totalCount) {
    t27 = <MapDiscoveryProgress foundCount={foundCount} totalCount={totalCount} onClick={t26} />;
    $[59] = foundCount;
    $[60] = t26;
    $[61] = totalCount;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== completionFilter || $[64] !== disableSearch || $[65] !== onSearchFocus || $[66] !== searchQuery) {
    t28 = !disableSearch && <SearchFilters searchQuery={searchQuery} onSearchChange={setSearchQuery} completionFilter={completionFilter} onCompletionFilterChange={setCompletionFilter} inputRef={searchInputRef} onFocus={onSearchFocus} />;
    $[63] = completionFilter;
    $[64] = disableSearch;
    $[65] = onSearchFocus;
    $[66] = searchQuery;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t27 || $[69] !== t28) {
    t29 = <FlexColumn gap="8" width="full">{t27}{t28}</FlexColumn>;
    $[68] = t27;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== checkedItems || $[72] !== editMode || $[73] !== groupedItems || $[74] !== handleToggleWithLogin || $[75] !== hasAnyItems || $[76] !== onRemovePin || $[77] !== removeLabel || $[78] !== selectMarkerInMap || $[79] !== selectSource || $[80] !== selectedMarkerId || $[81] !== t) {
    t30 = !hasAnyItems ? <FlexColumn width="full" align="center" className={css.raw({
      padding: "24"
    })}><Type variant="body-s" color="text.secondary">{t(_temp7)}</Type></FlexColumn> : Object.entries(groupedItems).map(t31 => {
      const [categoryTitle, items_0] = t31;
      return <Collapsible key={categoryTitle} variant="ghost" defaultOpen={true} className={{
        width: "full"
      }} iconProps={{
        size: "md"
      }} trigger={<Type variant="caption" color="white">{categoryTitle}</Type>} triggerClassName={{
        borderRadius: "8",
        justifyContent: "space-between",
        gap: "8"
      }} triggerStyle={{
        padding: "4px 4px 4px 8px"
      }} contentClassName={{
        padding: "0",
        paddingTop: "8"
      }}><FlexColumn width="full" gap="8">{items_0.map(item_1 => <ChecklistItemRow key={item_1.id} item={item_1} isChecked={checkedItems.has(item_1.id)} isSelected={selectedMarkerId === item_1.id} onToggle={handleToggleWithLogin} onShowOnMap={id => selectMarkerInMap(id, {
            flyTo: true,
            selectSource
          })} editMode={editMode} onRemove={onRemovePin} removeLabel={removeLabel} />)}</FlexColumn></Collapsible>;
    });
    $[71] = checkedItems;
    $[72] = editMode;
    $[73] = groupedItems;
    $[74] = handleToggleWithLogin;
    $[75] = hasAnyItems;
    $[76] = onRemovePin;
    $[77] = removeLabel;
    $[78] = selectMarkerInMap;
    $[79] = selectSource;
    $[80] = selectedMarkerId;
    $[81] = t;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== t30) {
    t31 = <FlexColumn gap="10" width="full">{t30}</FlexColumn>;
    $[83] = t30;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== editMode || $[86] !== feedbackSource || $[87] !== handleFeedbackRate || $[88] !== hasAnyItems || $[89] !== readOnly) {
    t32 = hasAnyItems && feedbackSource && !editMode && !readOnly && <ChecklistFeedback onRate={handleFeedbackRate} />;
    $[85] = editMode;
    $[86] = feedbackSource;
    $[87] = handleFeedbackRate;
    $[88] = hasAnyItems;
    $[89] = readOnly;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== backgroundImageUrl || $[92] !== beforeItems || $[93] !== headerAction || $[94] !== hero || $[95] !== isLoginModalOpen || $[96] !== isMobilePane || $[97] !== loginModalTrigger || $[98] !== onBack || $[99] !== onSearchFocus || $[100] !== setIsLoginModalOpen || $[101] !== t24 || $[102] !== t25 || $[103] !== t29 || $[104] !== t31 || $[105] !== t32) {
    t33 = <SidebarHeroFrame backgroundImageUrl={backgroundImageUrl} isMobilePane={isMobilePane} onBack={onBack} backLabel={t24} searchAriaLabel={t25} searchInputRef={searchInputRef} isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={setIsLoginModalOpen} loginModalTrigger={loginModalTrigger} onSearchFocus={onSearchFocus} gap="16" headerAction={headerAction}>{hero}{t29}{beforeItems}{t31}{t32}</SidebarHeroFrame>;
    $[91] = backgroundImageUrl;
    $[92] = beforeItems;
    $[93] = headerAction;
    $[94] = hero;
    $[95] = isLoginModalOpen;
    $[96] = isMobilePane;
    $[97] = loginModalTrigger;
    $[98] = onBack;
    $[99] = onSearchFocus;
    $[100] = setIsLoginModalOpen;
    $[101] = t24;
    $[102] = t25;
    $[103] = t29;
    $[104] = t31;
    $[105] = t32;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  return t33;
};
function _temp(state) {
  return state.auth.isAuthenticated;
}
function _temp2(item) {
  return item.id;
}
function _temp3(i) {
  return i.coordinates[0];
}
function _temp4(i_0) {
  return i_0.coordinates[1];
}
function _temp5($) {
  return $.session_history.back;
}
function _temp6($_0) {
  return $_0.checklist_pages.search_checklist_items_aria_label;
}
function _temp7($_1) {
  return $_1.no_results_found;
}